'use client';

// Core & Package Imports
import React, { useEffect, useState } from 'react';

// Function Imports
import { defaultStoreNumber } from '@/lib/constants/store';
import { hasVariantForStore, normalizeProduct } from '@/lib/functions/normalizeProduct';
import { getProductsByCode } from '@/lib/functions/getProductsByCode';
import { isPriceValid } from '@/lib/functions/isPriceValid';

// Component Imports
import ProductCard from '@/components/product/productCard';

// Type Imports
import type { NormalizedProduct } from '@/lib/types/products/normalizedProduct';
import type { RawProduct } from '@/lib/types/products/rawProduct';

// Style Imports
import styles from './featuredProducts.module.scss';

interface FeaturedProductsProps {
  title?: string;
  productCodes: string[];
}

/**
 * A responsive component to display featured items in a 5-column grid.
 * It fetches product data based on provided SKUs and displays them using ProductCard components.
 */
const FeaturedProducts: React.FC<FeaturedProductsProps> = ({ title, productCodes }) => {
  const [loading, setLoading] = useState(true);
  const [productCards, setProductCards] = useState<RawProduct[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      setLoading(true);

      if (productCodes.length > 0) {
        try {
          const fetchedProducts = await getProductsByCode(productCodes);
          if (isMounted && fetchedProducts && fetchedProducts.success) {
            setProductCards(fetchedProducts.payload);
          }
        } catch (error) {
          console.error("Error fetching product cards:", error);
          if (isMounted) setError("Failed to load products.");
        }
      }

      if (isMounted) {
        setLoading(false);
        setHasFetched(true);
      }
    };

    if (!hasFetched) {
      fetchProducts();
    }

    return () => {
      isMounted = false;
    };
  }, [productCodes, hasFetched]);

  const storeNumber = defaultStoreNumber;

  const ProductCards = loading
    ? []
    : productCards
      .filter((p): p is RawProduct => Boolean(p && hasVariantForStore(p, storeNumber)))
      .map((p) => normalizeProduct(p, storeNumber))
      .filter((c) => c.code != null && isPriceValid(c.pricing?.unitPrice));

  if (!loading && ProductCards.length === 0) return null;
  if (error || productCodes.length === 0) return null;

  return (
    <section className={styles.featuredItems}>
      <div className={styles.wrapper}>
        {title && <h2 className={styles.title}>{title}</h2>}
        <div className={styles.grid}>
          {(loading ? new Array(productCodes.length).fill(null) : ProductCards).map(
            (product: NormalizedProduct, index) => (
              <ProductCard
                key={`p-${index}`}
                product={product}
                loading={loading}
              />
            )
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
